<script setup lang="ts">
import { storeToRefs } from "pinia";
import type { ModelRef } from "vue";
import type LxcError from "~/core/models/LxcError.class";
import type { TreeNodes } from "~/core/models/TreeNodes.class.ts";
import type { Sector } from "~/modules/sector/models/Sector.interface";
import { useSectorStore } from "~/modules/sector/stores/useSectorStore";
import { mapSectorsToTreeNodes } from "~/modules/sector/utils/mapSectorsToTreeNodes";

const { t } = useI18n();

const { sectors: storedSectors } = storeToRefs(useSectorStore());

const props = defineProps<{
  disabled?: boolean;
  sector?: Sector;
  error?: LxcError;
}>();

const treeSelectKey = ref(0);
const lxcTreeNodes = ref<TreeNodes[]>([]);
const checkedSectorCodes: ModelRef<string[]> = defineModel<string[]>({
  default: [],
});

/**
 * Retrieve sectors from Webservices
 */
async function buildSectorTreeNodes() {
  if (props.sector) {
    lxcTreeNodes.value = mapSectorsToTreeNodes(props.sector);
  } else if (!props.sector && storedSectors.value) {
    lxcTreeNodes.value = mapSectorsToTreeNodes(storedSectors.value);
  }
}

function onCheckedSectorsChanged(sectorCodes: string[]) {
  checkedSectorCodes.value = sectorCodes;
}

onMounted(async () => {
  await buildSectorTreeNodes();
  treeSelectKey.value += 1;
});
</script>

<template>
  <div ref="treeSelectElement">
    <tree-select
      :key="treeSelectKey"
      :tree="lxcTreeNodes"
      :default-checked-values="checkedSectorCodes"
      :placeholder="t('layout.menu.sector.placeholder')"
      force-inheritance
      allow-empty-selection
      :disabled="disabled"
      :error="error"
      @on-checked-node-value-changed="onCheckedSectorsChanged"
    />
  </div>
</template>
